import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Activity,
  ArrowLeft,
  Database,

  Download,
  RefreshCw,
  Route as RouteIcon,
  Search,
  ShoppingCart,
  Upload,
  Users,
  X,

} from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { OverviewCharts } from "@/components/admin/intel/OverviewCharts";
import { SELLERS } from "@/lib/leads/sellers";
import { PeriodFilter, usePeriodFilter } from "@/components/admin/PeriodFilter";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { neutralizeCsvValue } from "@/lib/csv-safe";
import { UTM_CSV_HEADERS, utmCsvValues } from "@/lib/leads/utm-columns";
import {
  getLeadIntelOverview,
  getPersonProfile,
  listFunnelPeople,
  listPeriodJourneys,
  lookupPeopleBatch,
  rebuildLeadIntel,
  searchPeople,
  type ChannelFilter,
  type LeadIntelOverview,
  type OriginDecisionCell,
  type PersonTimelineEvent,
  type ProductScope,
} from "@/lib/leads/intel.functions";


export const Route = createFileRoute("/admin/inteligencia-leads")({
  head: () => ({
    meta: [
      { title: "Inteligência de Leads — Admin" },
      { name: "description", content: "Vendas por funil: origem e decisão de compra." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/inteligencia-leads">
      <IntelLeadsPage />
    </AdminGate>
  ),
});

const sellerName = (email: string) =>
  SELLERS.find((s) => s.email.toLowerCase() === email.toLowerCase())?.nome ?? email;

const num = (n: number | null | undefined, d = 0) =>

  n == null ? "—" : n.toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const moneyKpi = (n: number | null | undefined) =>
  n == null
    ? "—"
    : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const pct = (n: number | null | undefined) => (n == null ? "—" : `${n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const fmtDate = (s: string | null | undefined) =>
  !s ? "—" : new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-[11px] uppercase tracking-wide text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-white">{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-white/40">{hint}</div>}
    </div>
  );
}

function FilterBadge({ active, children }: { active?: boolean; children: React.ReactNode }) {
  return (
    <span className={`rounded border px-2 py-0.5 text-[11px] uppercase ${active ? "border-[#f5b022]/50 text-admin-accent" : "border-white/10 text-white/40"}`}>
      {children}
    </span>
  );
}

function IntelLeadsPage() {
  const qc = useQueryClient();
  const period = usePeriodFilter("month");
  const [productScope, setProductScope] = useState<ProductScope>("all");
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [includePartial, setIncludePartial] = useState(false);

  const overviewFn = useServerFn(getLeadIntelOverview);
  const overview = useQuery({
    queryKey: ["lead-intel", period.range.from, period.range.to, productScope, channel, includePartial],
    queryFn: () =>
      overviewFn({
        data: {
          fromIso: period.range.from,
          toIso: period.range.to,
          productScope,
          channel,
          includePartial,
        },
      }),
    retry: false,
    staleTime: 60_000,
  });

  const rebuildFn = useServerFn(rebuildLeadIntel);
  const rebuild = useMutation({
    mutationFn: () => rebuildFn(),
    onSuccess: () => {
      toast.success("Inteligência recalculada");
      qc.invalidateQueries({ queryKey: ["lead-intel"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = overview.data;
  const sources = data?.sources ?? [];
  const staleRun =
    !!data?.last_run?.finished_at &&
    !!data?.to &&
    new Date(data.last_run.finished_at).getTime() < new Date(data.to).getTime() - 6 * 3600_000;

  return (
    <div className="min-h-screen text-white">
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Voltar ao admin
            </Link>
            <h1 className="mt-2 text-2xl font-semibold">Inteligência Leads/CRM</h1>
            <p className="text-sm text-white/50">
              Qual funil traz o lead que compra e qual funil fecha a venda.
            </p>
          </div>
          <div className="text-right">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Link
                to="/admin/diagnostico-comercial"
                className="inline-flex items-center gap-2 rounded-lg border border-[#f5b022]/40 bg-[#f5b022]/10 px-3 py-2 text-xs font-semibold text-admin-accent transition hover:bg-[#f5b022]/20"
              >
                <Activity className="h-4 w-4" /> Módulo: Diagnóstico comercial (CRM)
              </Link>
              <Button
                size="sm"
                variant="outline"
                className="border-white/10 bg-white/5 hover:bg-white/10"
                disabled={rebuild.isPending}
                onClick={() => rebuild.mutate()}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${rebuild.isPending ? "animate-spin" : ""}`} />
                Recalcular agora
              </Button>
            </div>
            <div className="mt-1 text-[11px] text-white/40">
              Último recálculo: {fmtDate(data?.last_run?.finished_at)}
            </div>
          </div>

        </div>

        <div className="mb-4 flex flex-col gap-4 rounded-xl border border-white/10 bg-white/5 p-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <PeriodFilter {...period} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:w-[680px]">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wide text-white/50">Produto</label>
              <Select value={productScope} onValueChange={(v) => setProductScope(v as ProductScope)}>
                <SelectTrigger className="border-white/10 bg-white/5 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#13123a] text-white">
                  <SelectItem value="all">Tudo</SelectItem>
                  <SelectItem value="pos">Pós-graduações</SelectItem>
                  <SelectItem value="outros">Outros produtos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wide text-white/50">Canal</label>
              <Select value={channel} onValueChange={(v) => setChannel(v as ChannelFilter)}>
                <SelectTrigger className="border-white/10 bg-white/5 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#13123a] text-white">
                  <SelectItem value="all">Tudo</SelectItem>
                  <SelectItem value="CRM">CRM</SelectItem>
                  <SelectItem value="MKT">MKT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pb-2">
              <Switch checked={includePartial} onCheckedChange={setIncludePartial} id="partial" />
              <label htmlFor="partial" className="text-sm text-white/70">
                Incluir capturas parciais
              </label>
            </div>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
          <Kpi label="Pessoas únicas" value={num(data?.totals.identities)} hint="base completa" />
          <Kpi label="Conversões no período" value={num(data?.totals.leads_period)} />
          <Kpi label="Vendas no período" value={num(data?.totals.sales_period)} />
          <Kpi label="Receita no período" value={moneyKpi(data?.totals.revenue_period)} hint={`ticket ${moneyKpi(data?.totals.avg_ticket)}`} />
          <Kpi
            label="Receita CRM / MKT"
            value={`${moneyKpi(data?.totals.crm_revenue)} / ${moneyKpi(data?.totals.mkt_revenue)}`}
            hint={`${num(data?.totals.crm_sales)} / ${num(data?.totals.mkt_sales)} vendas`}
          />
          <Kpi label="Vendas mapeadas" value={`${num(data?.totals.sales_period && data?.totals.unmapped_sales != null ? data.totals.sales_period - data.totals.unmapped_sales : null)}`} hint={`${pct(data?.totals.mapped_rate)} com origem`} />
          <Kpi label="Sem origem" value={num(data?.totals.unmapped_sales)} hint="vendas sem captura" />
          <Kpi label="Pós / Outros" value={`${num(data?.totals.pos_sales)} / ${num(data?.totals.outros_sales)}`} />
        </div>


        <Tabs defaultValue="visao">
          <TabsList className="bg-white/5">
            <TabsTrigger value="visao">Visão geral</TabsTrigger>
            <TabsTrigger value="funis">Funis</TabsTrigger>
            <TabsTrigger value="jornadas">Jornadas do período</TabsTrigger>
            <TabsTrigger value="pessoas">Pessoas</TabsTrigger>
          </TabsList>

          <TabsContent value="visao" className="mt-4">
            {overview.isLoading ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center text-sm text-white/50">
                Carregando inteligência…
              </div>
            ) : !data ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center text-sm text-white/50">
                Não foi possível carregar os dados do período.
              </div>
            ) : (
              <>
                {staleRun ? (
                  <div className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                    O último recálculo da inteligência é anterior ao fim do período consultado. Clique em
                    “Recalcular agora” para atualizar origem, decisão e receita atribuída.
                  </div>
                ) : null}
                <OverviewCharts data={data} />
              </>
            )}
          </TabsContent>

          <TabsContent value="funis" className="mt-4 space-y-6">
            <FunnelsTable sources={sources} from={data?.from} to={data?.to} filters={data?.filters} />
            <MatrixSection matrix={data?.matrix ?? []} />
            <CombosSection combos={data?.combos ?? []} />
          </TabsContent>

          <TabsContent value="jornadas" className="mt-4">
            <JourneysTab
              fromIso={period.range.from ?? ""}
              toIso={period.range.to ?? ""}

              includePartial={includePartial}
            />
          </TabsContent>

          <TabsContent value="pessoas" className="mt-4">
            <PeopleTab />
          </TabsContent>
        </Tabs>

      </div>
    </div>
  );
}

type FunnelRow = LeadIntelOverview["sources"][number];

function FunnelsTable({
  sources,
  from,
  to,
  filters,
}: {
  sources: FunnelRow[];
  from?: string;
  to?: string;
  filters?: LeadIntelOverview["filters"];
}) {
  const [drill, setDrill] = useState<{ source: string; role: "origin" | "decision" } | null>(null);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-white/60">Vendas por funil</h2>
        <FilterBadge active={filters?.productScope !== "all"}>{filters?.productScope === "pos" ? "Pós" : filters?.productScope === "outros" ? "Outros" : "Tudo"}</FilterBadge>
        <FilterBadge active={filters?.channel !== "all"}>{filters?.channel === "CRM" ? "CRM" : filters?.channel === "MKT" ? "MKT" : "Tudo"}</FilterBadge>
        {filters?.includePartial && <FilterBadge active>Parciais</FilterBadge>}
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-white/50">
            <tr>
              <th className="p-2 text-left">Funil</th>
              <th className="p-2 text-right">Leads</th>
              <th className="p-2 text-right">Completos</th>
              <th className="p-2 text-right">Pessoas</th>
              <th className="p-2 text-right text-emerald-300">ORIGEM</th>
              <th className="p-2 text-right text-sky-300">DECISÃO</th>
              <th className="p-2 text-right">CRM</th>
              <th className="p-2 text-right">MKT</th>
              <th className="p-2 text-right">Conv%</th>
              <th className="p-2 text-right">Dias</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((r) => (
              <tr key={r.source} className="border-t border-white/5">
                <td className="p-2 font-medium">{r.source}</td>
                <td className="p-2 text-right">{num(r.leads)}</td>
                <td className="p-2 text-right">{num(r.full)}</td>
                <td className="p-2 text-right">{num(r.people)}</td>
                <td className="p-2 text-right">
                  <button
                    onClick={() => setDrill({ source: r.source, role: "origin" })}
                    className="text-emerald-300 hover:underline"
                  >
                    {num(r.origin_sales)}
                  </button>
                </td>
                <td className="p-2 text-right">
                  <button
                    onClick={() => setDrill({ source: r.source, role: "decision" })}
                    className="text-sky-300 hover:underline"
                  >
                    {num(r.decision_sales)}
                  </button>
                </td>
                <td className="p-2 text-right">{num(r.crm_sales)}</td>
                <td className="p-2 text-right">{num(r.mkt_sales)}</td>
                <td className="p-2 text-right">{pct(r.conv_rate)}</td>
                <td className="p-2 text-right">{r.avg_days == null ? "—" : `${r.avg_days}d`}</td>
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td colSpan={10} className="p-4 text-center text-white/40">
                  Sem dados no período. Rode “Recalcular agora”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-white/40">
        ORIGEM = primeiro toque da vida da pessoa. DECISÃO = último toque até 42 dias antes da compra. Conv% = vendas ORIGEM ÷ pessoas únicas do funil.
      </p>

      <FunnelPeopleDialog drill={drill} onClose={() => setDrill(null)} from={from} to={to} />
    </div>
  );
}

function FunnelPeopleDialog({
  drill,
  onClose,
  from,
  to,
}: {
  drill: { source: string; role: "origin" | "decision" } | null;
  onClose: () => void;
  from?: string;
  to?: string;
}) {
  const listFn = useServerFn(listFunnelPeople);
  const profileFn = useServerFn(getPersonProfile);
  const [selected, setSelected] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["funnel-people", drill?.source, drill?.role, from, to],
    queryFn: () =>
      drill
        ? listFn({ data: { source: drill.source, role: drill.role, fromIso: from, toIso: to } })
        : Promise.resolve({ people: [] }),
    enabled: !!drill,
    retry: false,
  });

  const profile = useQuery({
    queryKey: ["person-profile", selected],
    queryFn: () => profileFn({ data: { identityId: selected! } }),
    enabled: !!selected,
    retry: false,
  });

  return (
    <Dialog open={!!drill} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto border-white/10 bg-[#13123a] text-white">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {drill?.role === "origin" ? "ORIGEM" : "DECISÃO"}: {drill?.source}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-2">
          {(q.data?.people ?? []).map((p) => (
            <button
              key={p.identity_id}
              onClick={() => setSelected(p.identity_id)}
              className="w-full rounded-lg border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{p.name ?? "—"}</div>
                  <div className="text-[11px] text-white/50">
                    {p.email ?? p.phone ?? "—"} · {p.capture_count} conversões
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="text-emerald-300">O: {p.origin_sales}</div>
                  <div className="text-sky-300">D: {p.decision_sales}</div>
                </div>
              </div>
            </button>
          ))}
          {q.isLoading && <div className="text-sm text-white/40">Carregando…</div>}
          {!q.isLoading && (q.data?.people ?? []).length === 0 && (
            <div className="text-sm text-white/40">Nenhuma pessoa encontrada.</div>
          )}
        </div>

        {selected && (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-semibold">Ficha da pessoa</h3>
              <Button size="icon" variant="ghost" onClick={() => setSelected(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {profile.isLoading && <div className="text-sm text-white/40">Carregando…</div>}
            {profile.data && <PersonProfileView p={profile.data} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MatrixSection({ matrix }: { matrix: OriginDecisionCell[] }) {
  if (matrix.length === 0) return null;

  const origins = Array.from(new Set(matrix.map((m: OriginDecisionCell) => m.origin)));
  const decisions = Array.from(new Set(matrix.map((m: OriginDecisionCell) => m.decision)));

  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-white/60">
        Matriz origem × decisão
      </h2>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-white/50">
            <tr>
              <th className="p-2 text-left">Origem \ Decisão</th>
              {decisions.map((d: string) => (
                <th key={d} className="p-2 text-right">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {origins.map((o: string) => (
              <tr key={o} className="border-t border-white/5">
                <td className="p-2 font-medium">{o}</td>
                {decisions.map((d: string) => {
                  const cell = matrix.find((m: OriginDecisionCell) => m.origin === o && m.decision === d);
                  return (
                    <td key={d} className={`p-2 text-right ${o === d ? "text-emerald-300" : ""}`}>
                      {cell ? num(cell.sales) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-white/40">
        Diagonal verde = funil que trouxe e fechou sozinho. Fora da diagonal = um funil alimentou outro.
      </p>
    </div>
  );
}

type ComboRow = LeadIntelOverview["combos"][number];

function CombosSection({ combos }: { combos: ComboRow[] }) {
  if (combos.length === 0) return null;
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white/60">
        <RouteIcon className="h-4 w-4" /> Origem → Decisão
      </h2>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-white/50">
            <tr>
              <th className="p-2 text-left">Caminho</th>
              <th className="p-2 text-right">Vendas</th>
            </tr>
          </thead>
          <tbody>
            {combos.map((c) => (
              <tr key={c.path} className="border-t border-white/5">
                <td className="p-2">{c.path}</td>
                <td className="p-2 text-right">{num(c.sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ ficha pessoa

function PeopleTab() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [batchText, setBatchText] = useState("");
  const [batchMode, setBatchMode] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);


  const searchFn = useServerFn(searchPeople);
  const profileFn = useServerFn(getPersonProfile);
  const batchFn = useServerFn(lookupPeopleBatch);

  const results = useQuery({
    queryKey: ["people-search", submitted],
    queryFn: () => searchFn({ data: { query: submitted } }),
    enabled: submitted.length >= 2,
    retry: false,
  });

  const batchResult = useQuery({
    queryKey: ["people-batch", batchText],
    queryFn: () => {
      const keys = batchText
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      return batchFn({ data: { keys } });
    },
    enabled: batchMode && batchText.length >= 3,
    retry: false,
  });

  const profile = useQuery({
    queryKey: ["person-profile", selected],
    queryFn: () => profileFn({ data: { identityId: selected! } }),
    enabled: !!selected,
    retry: false,
  });

  const exportBatchCsv = () => {
    const rows = batchResult.data?.people ?? [];
    if (!rows.length) return;
    const header = ["Nome", "Email", "Telefone", "Primeiro contato", "Conversões", "Vendas origem", "Vendas decisão", "Pós", "Outros", ...UTM_CSV_HEADERS];
    const lines = rows.map((r) =>
      [r.name, r.email, r.phone, r.first_seen_at, r.capture_count, r.origin_sales, r.decision_sales, r.pos_sales, r.outros_sales, ...utmCsvValues(r)]
        .map((v) => `"${neutralizeCsvValue(String(v ?? "")).replace(/"/g, '""')}"`)
        .join(";"),
    );
    const csv = [header.join(";"), ...lines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pessoas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
        <div className="mb-3 flex gap-2">
          <Button
            size="sm"
            variant={batchMode ? "outline" : "default"}
            className={batchMode ? "border-white/10 bg-white/5" : "bg-[#f5b022] text-[#13123a]"}
            onClick={() => setBatchMode(false)}
          >
            Busca
          </Button>
          <Button
            size="sm"
            variant={!batchMode ? "outline" : "default"}
            className={!batchMode ? "border-white/10 bg-white/5" : "bg-[#f5b022] text-[#13123a]"}
            onClick={() => setBatchMode(true)}
          >
            Lote
          </Button>
        </div>

        {!batchMode ? (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSelected(null);
              setSubmitted(query.trim());
            }}
          >
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="nome, e-mail ou telefone"
              className="border-white/10 bg-white/5 text-white"
            />
            <Button size="icon" type="submit" className="bg-[#f5b022] text-[#13123a] hover:bg-[#e09f17]">
              <Search className="h-4 w-4" />
            </Button>
          </form>
        ) : (
          <div className="space-y-2">
            <textarea
              value={batchText}
              onChange={(e) => setBatchText(e.target.value)}
              placeholder="cole e-mails ou telefones separados por vírgula, ponto-e-vírgula ou quebra de linha"
              rows={6}
              className="w-full rounded-md border border-white/10 bg-white/5 p-2 text-sm text-white placeholder:text-white/30"
            />
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-white/15 bg-white/5 px-3 py-2 text-xs text-white/60 hover:bg-white/10">
              <Upload className="h-4 w-4" /> Importar planilha (.xlsx, .csv)
              <input
                type="file"
                accept=".xlsx,.xls,.csv,.txt"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  setImportError(null);
                  try {
                    const XLSX = await import("xlsx");
                    const buf = await file.arrayBuffer();
                    const wb = XLSX.read(buf, { type: "array" });
                    const keys = new Set<string>();
                    for (const name of wb.SheetNames) {
                      const rows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name]!, { header: 1 });
                      for (const row of rows) {
                        for (const cell of row ?? []) {
                          const v = String(cell ?? "").trim();
                          if (!v) continue;
                          if (v.includes("@")) keys.add(v.toLowerCase());
                          else if (v.replace(/\D/g, "").length >= 10) keys.add(v);
                        }
                      }
                    }
                    if (!keys.size) {
                      setImportError("Nenhum e-mail ou telefone encontrado na planilha.");
                      return;
                    }
                    setBatchText(Array.from(keys).join("\n"));
                  } catch {
                    setImportError("Não foi possível ler o arquivo.");
                  }
                }}
              />
            </label>
            {importError && <div className="text-[11px] text-rose-300">{importError}</div>}
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-[#f5b022] text-[#13123a]"
                onClick={() => batchResult.refetch()}
                disabled={batchText.length < 3}
              >
                <Search className="mr-1 h-4 w-4" /> Buscar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-white/10 bg-white/5"
                onClick={exportBatchCsv}
                disabled={!(batchResult.data?.people ?? []).length}
              >
                <Download className="mr-1 h-4 w-4" /> CSV
              </Button>
            </div>
            {batchText.length > 0 && (
              <div className="text-[11px] text-white/40">
                {batchText.split(/[,;\n]+/).filter((s) => s.trim()).length} contatos na lista
              </div>
            )}
          </div>

        )}

        <div className="mt-3 space-y-1">
          {!batchMode &&
            (results.data?.people ?? []).map((row) => (
              <button
                key={row.id}
                onClick={() => setSelected(row.id)}
                className={`w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-white/10 ${
                  selected === row.id ? "bg-white/10" : ""
                }`}
              >
                <div className="font-medium">{row.primary_name ?? "—"}</div>
                <div className="text-[11px] text-white/45">
                  {row.primary_email ?? row.primary_phone ?? "—"} · {row.capture_count} conversões
                </div>
              </button>
            ))}
          {batchMode &&
            (batchResult.data?.people ?? []).map((row) => (
              <button
                key={row.id}
                onClick={() => setSelected(row.id)}
                className={`w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-white/10 ${
                  selected === row.id ? "bg-white/10" : ""
                }`}
              >
                <div className="font-medium">{row.name ?? "—"}</div>
                <div className="text-[11px] text-white/45">
                  {row.email ?? row.phone ?? "—"} · O:{row.origin_sales} D:{row.decision_sales}
                </div>
              </button>
            ))}
          {!batchMode && submitted.length >= 2 && (results.data?.people ?? []).length === 0 && !results.isLoading && (
            <div className="px-2 py-4 text-center text-xs text-white/40">Nada encontrado.</div>
          )}
          {batchMode &&
            batchResult.isSuccess &&
            (batchResult.data?.people ?? []).length === 0 && (
              <div className="px-2 py-4 text-center text-xs text-white/40">
                Nenhum contato localizado.
              </div>
            )}
          {batchMode && (batchResult.data?.notFound ?? []).length > 0 && (
            <div className="rounded border border-white/10 bg-white/5 p-2">
              <div className="text-[10px] uppercase text-white/40">Não localizados ({batchResult.data!.notFound.length})</div>
              <div className="mt-1 text-[11px] text-white/50">
                {batchResult.data!.notFound.slice(0, 10).join(", ")}
                {batchResult.data!.notFound.length > 10 && " …"}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        {!selected && <div className="text-sm text-white/40">Busque uma pessoa para ver a jornada completa.</div>}
        {profile.isLoading && <div className="text-sm text-white/40">Carregando…</div>}
        {profile.data && <PersonProfileView p={profile.data} />}
      </div>
    </div>
  );
}

function PersonProfileView({ p }: { p: Awaited<ReturnType<typeof getPersonProfile>> }) {
  return (
    <>
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-admin-accent" />
        <h3 className="text-lg font-semibold">{p.identity.primary_name ?? "—"}</h3>
        {p.identity.best_classificacao && (
          <span className="rounded border border-white/15 px-1.5 py-0.5 text-[10px] uppercase">
            {p.identity.best_classificacao}
          </span>
        )}
      </div>
      <div className="mt-1 grid gap-1 text-xs text-white/60 md:grid-cols-2">
        <div>E-mails: {p.emails.join(", ") || "—"}</div>
        <div>Telefones: {p.phones.join(", ") || "—"}</div>
        <div>1º contato: {fmtDate(p.identity.first_seen_at)} · {p.identity.first_origem ?? "—"}</div>
        <div>
          1ª origem paga: {p.identity.first_utm_source ?? "—"} / {p.identity.first_utm_campaign ?? "—"}
        </div>
        {p.rd && <div className="md:col-span-2">RD Station: {p.rd.tags.join(", ") || "sem tags"}</div>}
      </div>

      <TimelineList timeline={p.timeline} />
    </>
  );
}

function TimelineList({ timeline }: { timeline: PersonTimelineEvent[] }) {
  return (
    <ol className="mt-4 space-y-2 border-l border-white/10 pl-4">
      {timeline.map((ev, i) => (
        <li key={i} className="relative text-sm">
          <span
            className={`absolute -left-[21px] top-1.5 h-2 w-2 rounded-full ${
              ev.kind === "sale" ? "bg-emerald-400" : "bg-[#f5b022]"
            }`}
          />
          {ev.kind === "capture" ? (
            <div>
              <span className="text-white/45">{fmtDate(ev.at)}</span>{" "}
              <span className="font-medium">{ev.source ?? "—"}</span>
              {ev.is_partial && <span className="ml-1 text-[10px] text-admin-accent">(parcial)</span>}
              <div className="text-[11px] text-white/45">
                {[ev.pos_nome, ev.area, ev.classificacao, ev.landing_path, ev.utm_campaign, ev.seller]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </div>
            </div>
          ) : (
            <div>
              <span className="text-white/45">{fmtDate(ev.at)}</span>{" "}
              <span className="inline-flex items-center gap-1 font-medium text-emerald-300">
                <ShoppingCart className="h-3 w-3" /> {ev.product_name ?? "compra"}
              </span>{" "}
              <span className="text-white/60">{ev.channel}</span>
              {ev.seller && (
                <span className="ml-2 rounded border border-sky-400/30 bg-sky-400/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-200">
                  {sellerName(ev.seller)}
                </span>
              )}
              <div className="text-[11px] text-white/45">
                Crédito: {ev.credited.map((c) => `${c.source ?? "—"} (${c.model})`).join(" · ") || "sem captura"}
              </div>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

// ------------------------------------------- jornadas de todos os leads do período

function JourneysTab({
  fromIso,
  toIso,
  includePartial,
}: {
  fromIso: string;
  toIso: string;
  includePartial: boolean;
}) {
  const [onlyBuyers, setOnlyBuyers] = useState(false);
  const [page, setPage] = useState(0);
  const perPage = 50;
  const journeysFn = useServerFn(listPeriodJourneys);

  const q = useQuery({
    queryKey: ["period-journeys", fromIso, toIso, includePartial, onlyBuyers, page],
    queryFn: () =>
      journeysFn({
        data: { fromIso, toIso, includePartial, onlyBuyers, limit: perPage, offset: page * perPage },
      }),
    retry: false,
    staleTime: 60_000,
  });

  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
        <div className="text-sm text-white/70">
          {q.isLoading ? "Carregando…" : `${num(total)} pessoas converteram no período`}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="onlyBuyers"
            checked={onlyBuyers}
            onCheckedChange={(v) => {
              setOnlyBuyers(v);
              setPage(0);
            }}
          />
          <label htmlFor="onlyBuyers" className="text-sm text-white/70">
            Só quem comprou
          </label>
        </div>
      </div>

      {(q.data?.people ?? []).map((p) => (
        <div key={p.identity_id} className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Users className="h-4 w-4 text-admin-accent" />
            <span className="font-semibold">{p.name ?? "—"}</span>
            <span className="text-xs text-white/50">{p.email ?? "—"}</span>
            <span className="text-xs text-white/40">{p.phone ?? ""}</span>
            {p.sales_count > 0 && (
              <span className="rounded border border-emerald-400/30 bg-emerald-400/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-200">
                {p.sales_count} compra{p.sales_count > 1 ? "s" : ""}
              </span>
            )}
            <span className="ml-auto text-[11px] text-white/40">
              conversão no período: {fmtDate(p.converted_at)}
            </span>
          </div>
          <TimelineList timeline={p.timeline} />
        </div>
      ))}

      {!q.isLoading && (q.data?.people ?? []).length === 0 && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/40">
          Nenhuma conversão no período selecionado.
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <Button
            size="sm"
            variant="outline"
            className="border-white/10 bg-white/5"
            disabled={page === 0}
            onClick={() => setPage((n) => n - 1)}
          >
            Anterior
          </Button>
          <span className="text-white/50">
            {page + 1} / {pages}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-white/10 bg-white/5"
            disabled={page + 1 >= pages}
            onClick={() => setPage((n) => n + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}

